/**
 * Simple inline Markdown renderer for support conversations.
 * Supports: **bold**, *italic*, `code`, and auto-linked URLs.
 * No external dependencies.
 */
export function MarkdownText({text, className, linkClassName}: { text: string; className?: string; linkClassName?: string }) {
  const elements = parseInlineMarkdown(text, linkClassName);
  return <span className={className}>{elements}</span>;
}

function parseInlineMarkdown(text: string, linkClassName?: string): (string | JSX.Element)[] {
  // Pattern: **bold**, *italic*, ~~strikethrough~~, `code`, URLs
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|~~(.+?)~~|`(.+?)`|(https?:\/\/[^\s<>]+))/g;
  const result: (string | JSX.Element)[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      result.push(text.substring(lastIndex, match.index));
    }

    if (match[2]) {
      // **bold**
      result.push(<strong key={key++}>{match[2]}</strong>);
    } else if (match[3]) {
      // *italic*
      result.push(<em key={key++}>{match[3]}</em>);
    } else if (match[4]) {
      // ~~strikethrough~~
      result.push(<s key={key++} className="text-gray-400">{match[4]}</s>);
    } else if (match[5]) {
      // `code`
      result.push(
        <code key={key++} className="px-1 py-0.5 bg-gray-100 rounded text-[90%] font-mono">
          {match[5]}
        </code>
      );
    } else if (match[6]) {
      // URL
      result.push(
        <a key={key++} href={match[6]} target="_blank" rel="noopener noreferrer" className={linkClassName || "text-primary underline"}>
          {match[6]}
        </a>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    result.push(text.substring(lastIndex));
  }

  return result.length > 0 ? result : [text];
}
